import { useListFilters } from "../../hooks/useListState.js";
import { validateListFilters } from "../../lib/listState.js";
import { useMemo } from "react";
import { RotateCcw, Settings } from "lucide-react";
import { Button, Card, Select } from "antd";
import { AppEmpty } from "../../components/Feedback.jsx";
import { CURRENT_YEAR, COURSE_TERM_OPTIONS } from "../../utils/academy.js";
import { classificationLabel } from "../../utils/courseTerms.js";
import { timetableTermOptions, timetableTermType } from "../../utils/timetableTerms.js";
import { AcademyWeeklyGrid } from "./AcademyWeeklyGrid.jsx";
import { TodayTimetableCards } from "./TodayTimetableCards.jsx";

export function TimetablePage({ store, setActiveView }) {
  const { state } = store;
  const courses = state.courseGroups || [];
  const subjects = state.subjects || [];
  const teachers = state.teachers || [];
  const courseTerms = state.courseTerms?.length ? state.courseTerms : COURSE_TERM_OPTIONS;
  const years = useMemo(() => [...new Set([CURRENT_YEAR, ...courses.map((course) => String(course.year || CURRENT_YEAR))])].sort((a, b) => b.localeCompare(a)), [courses]);
  const yearOptions = useMemo(() => years.map((year) => ({ value: year, label: year })), [years]);
  const initialTerm = timetableTermType(courses.find(course => course.active !== false && String(course.year || CURRENT_YEAR) === CURRENT_YEAR) || {}, courseTerms) || "first_semester";
  const defaults = { year: CURRENT_YEAR, term: initialTerm };
  const validate = v => {
    const valid = validateListFilters(v, defaults, { year: (store.loading || !!store.error) ? null : years });
    return (store.loading || !!store.error) ? valid : validateListFilters(valid, defaults, { term: timetableTermOptions(courses, courseTerms, valid.year).map(o => o.value) });
  };
  const [filterInput, setFilterInput] = useListFilters("list:timetable:input", defaults, validate);
  const [filter, setFilter] = useListFilters("list:timetable:filter", defaults, validate);
  const termOptions = timetableTermOptions(courses, courseTerms, filterInput.year);

  const timetableCourses = useMemo(() => courses
    .filter((course) => course.active !== false)
    .filter((course) => String(course.year || CURRENT_YEAR) === filter.year)
    .filter((course) => timetableTermType(course, courseTerms) === filter.term)
    .sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")),
  [courses, courseTerms, filter]);

  const title = `${filter.year}년 ${classificationLabel(filter.term)} 시간표`;

  function updateFilter(values) {
    const nextFilter = { ...filterInput, ...values };
    if (!timetableTermOptions(courses, courseTerms, nextFilter.year).some(option => option.value === nextFilter.term)) nextFilter.term = "first_semester";
    setFilterInput(nextFilter);
    setFilter(nextFilter);
  }

  function resetFilter() {
    const nextFilter = { year: CURRENT_YEAR, term: initialTerm };
    setFilterInput(nextFilter);
    setFilter(nextFilter);
  }

  return (
    <div className="page-stack">
      <Card title="시간표 검색" className="academy-management-card">
        <div className="student-filter-form">
          <div className="student-filter-inline">
            <div className={`student-filter-control${filterInput.year !== CURRENT_YEAR ? " is-active" : ""}`}><span>년도:</span><Select value={filterInput.year} onChange={(year) => updateFilter({ year })} options={yearOptions} /></div>
            <div className={`student-filter-control${filterInput.term !== initialTerm ? " is-active" : ""}`}><span>구분:</span><Select value={filterInput.term} onChange={(term) => updateFilter({ term })} options={termOptions} /></div>
            <Button htmlType="button" className="student-filter-reset" onClick={resetFilter} icon={<RotateCcw size={14} />}>필터 초기화</Button>
          </div>
        </div>
      </Card>

      <Card className="academy-management-card timetable-main-card">
        <div className="academy-tab-panel">
          <div className="section-toolbar">
            <h3>{title}</h3>
            <Button className="section-toolbar-primary" icon={<Settings size={16} />} onClick={() => setActiveView("academy")}>수업 관리로 이동</Button>
          </div>
          <TodayTimetableCards courses={timetableCourses} subjects={subjects} teachers={teachers} />
          {timetableCourses.length ? (
            <AcademyWeeklyGrid colorCourses={courses} courses={timetableCourses} subjects={subjects} teachers={teachers} />
          ) : (
            <AppEmpty description="조건에 맞는 수업이 없습니다." />
          )}
        </div>
      </Card>
    </div>
  );
}
